import styled, {keyframes} from 'styled-components';
import {ReactNode, useEffect, useState} from 'react';
import {Spinner} from './Spinner';

const indeterminate = keyframes`
  0% { transform: translateX(-100%) scaleX(0.35); }
  50% { transform: translateX(20%) scaleX(0.6); }
  100% { transform: translateX(120%) scaleX(0.35); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate3d(0, 6px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translate3d(0, 0, 0); }
  to { opacity: 0; transform: translate3d(0, -4px, 0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const ProgressTrack = styled.div<{$height: number}>`
  position: relative;
  width: 100%;
  height: ${(p) => p.$height}px;
  border-radius: 999px;
  background: rgba(47, 48, 113, 0.08);
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: ${(p) => p.theme.gradients.brand};
  transform-origin: left center;
  animation: ${indeterminate} 1.4s ${(p) => p.theme.motion.easing.standard}
    infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scaleX(0.4);
  }
`;

const ProgressFill = styled.div<{$value: number}>`
  height: 100%;
  width: ${(p) => Math.min(100, Math.max(0, p.$value))}%;
  border-radius: 999px;
  background: ${(p) => p.theme.gradients.brand};
  transition: width ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out};
`;

interface ProgressBarProps {
  value?: number;
  height?: number;
  className?: string;
}

export const ProgressBar = ({
  value,
  height = 4,
  className,
}: ProgressBarProps) => (
  <ProgressTrack
    $height={height}
    className={className}
    role="progressbar"
    aria-valuenow={value}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    {value === undefined ? (
      <ProgressBarInner />
    ) : (
      <ProgressFill $value={value} />
    )}
  </ProgressTrack>
);

const StateWrapper = styled.div<{$minHeight: number}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  min-height: ${(p) => p.$minHeight}px;
  padding: 32px 16px;
  text-align: center;
  animation: ${fadeIn} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.p`
  color: ${(p) => p.theme.colors.primary500};
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

const Description = styled.p`
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 13px;
  margin: 0;
  max-width: 340px;
  line-height: 1.6;
`;

interface LoadingStateProps {
  title?: string;
  description?: string;
  minHeight?: number;
  showProgress?: boolean;
  className?: string;
}

export const LoadingState = ({
  title,
  description,
  minHeight = 200,
  showProgress = false,
  className,
}: LoadingStateProps) => (
  <StateWrapper
    $minHeight={minHeight}
    className={className}
    role="status"
    aria-live="polite"
  >
    <Spinner />
    {title ? <Title>{title}</Title> : null}
    {description ? <Description>{description}</Description> : null}
    {showProgress ? (
      <div style={{width: 'min(240px, 70%)'}}>
        <ProgressBar />
      </div>
    ) : null}
  </StateWrapper>
);

const DotsWrapper = styled.div<{$size: number; $color?: string}>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => Math.round(p.$size * 0.6)}px;

  span {
    width: ${(p) => p.$size}px;
    height: ${(p) => p.$size}px;
    border-radius: 50%;
    background: ${(p) => p.$color ?? p.theme.colors.primary500};
    animation: ${dotBounce} 1.2s ${(p) => p.theme.motion.easing.standard}
      infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.16s;
  }
  span:nth-child(3) {
    animation-delay: 0.32s;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      opacity: 0.6;
    }
  }
`;

interface LoadingDotsProps {
  size?: number;
  color?: string;
  className?: string;
}

export const LoadingDots = ({size = 6, color, className}: LoadingDotsProps) => (
  <DotsWrapper $size={size} $color={color} className={className} aria-hidden>
    <span />
    <span />
    <span />
  </DotsWrapper>
);

const FadeLayer = styled.div<{$leaving: boolean; $delay: number}>`
  animation: ${(p) => (p.$leaving ? fadeOut : fadeIn)}
    ${(p) =>
      p.$leaving ? p.theme.motion.duration.fast : p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
  animation-delay: ${(p) => p.$delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

interface FadeProps {
  children: ReactNode;
  leaving?: boolean;
  delay?: number;
  className?: string;
}

export const Fade = ({
  children,
  leaving = false,
  delay = 0,
  className,
}: FadeProps) => (
  <FadeLayer $leaving={leaving} $delay={delay} className={className}>
    {children}
  </FadeLayer>
);

interface LoadingSwapProps {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  exitDuration?: number;
}

export const LoadingSwap = ({
  loading,
  skeleton,
  children,
  exitDuration = 200,
}: LoadingSwapProps) => {
  const [phase, setPhase] = useState<'skeleton' | 'leaving' | 'content'>(
    loading ? 'skeleton' : 'content'
  );

  useEffect(() => {
    if (loading) {
      setPhase('skeleton');
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    setPhase((current) => {
      if (current === 'content') {
        return current;
      }
      timeoutId = setTimeout(() => setPhase('content'), exitDuration);
      return 'leaving';
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading, exitDuration]);

  if (phase === 'content') {
    return <Fade>{children}</Fade>;
  }

  return <Fade leaving={phase === 'leaving'}>{skeleton}</Fade>;
};

const OverlayWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: grid;
  place-items: center;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${fadeIn} ${(p) => p.theme.motion.duration.base}
    ${(p) => p.theme.motion.easing.out} both;
`;

interface LoadingOverlayProps {
  visible: boolean;
  title?: string;
  children?: ReactNode;
}

export const LoadingOverlay = ({
  visible,
  title,
  children,
}: LoadingOverlayProps) => {
  if (!visible) {
    return null;
  }
  return (
    <OverlayWrapper role="status" aria-live="polite">
      {children ?? <LoadingState title={title} minHeight={0} />}
    </OverlayWrapper>
  );
};
